// src/pages/administrasi/TemplateManager.jsx
import { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../utils/config";
import CustomSelect from "../../components/CustomSelect";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      data.detail || data.non_field_errors?.[0] || JSON.stringify(data) || "Request failed"
    );
  }
  return res.status === 204 ? null : res.json();
}

const MODULE_OPTIONS = [
  { value: "sekretaris", label: "Sekretaris" },
  { value: "bendahara", label: "Bendahara" },
];

const CATEGORY_OPTIONS_BY_MODULE = {
  sekretaris: [
    { value: "form_pengajuan", label: "Form Pengajuan" },
    { value: "surat_masuk", label: "Surat Masuk" },
    { value: "surat_keluar", label: "Surat Keluar" },
  ],
  bendahara: [
    { value: "pengajuan_dana", label: "Pengajuan Dana" },
    { value: "kas_masuk", label: "Kas Masuk" },
    { value: "kas_keluar", label: "Kas Keluar" },
    { value: "lpj", label: "LPJ" },
  ],
};

// Urutan & gaya seksi di halaman daftar: tiap modul dipisah supaya template
// Bendahara dan Sekretaris tidak tercampur dalam satu list.
const MODULE_SECTIONS = [
  {
    value: "bendahara",
    label: "Bendahara",
    desc: "Pengajuan dana, kas masuk, kas keluar, LPJ",
    dot: "bg-emerald-500",
    border: "border-l-emerald-500",
  },
  {
    value: "sekretaris",
    label: "Sekretaris",
    desc: "Form pengajuan, surat masuk, surat keluar",
    dot: "bg-sky-500",
    border: "border-l-sky-500",
  },
];

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Teks singkat" },
  { value: "textarea", label: "Teks panjang" },
  { value: "date", label: "Tanggal" },
  { value: "number", label: "Angka" },
];

const CATEGORY_LABELS = {
  form_pengajuan: "Form Pengajuan",
  surat_masuk: "Surat Masuk",
  surat_keluar: "Surat Keluar",
  pengajuan_dana: "Pengajuan Dana",
  kas_masuk: "Kas Masuk",
  kas_keluar: "Kas Keluar",
  lpj: "LPJ",
};

const emptyField = () => ({ key: "", label: "", type: "text", required: false });

export default function TemplateManager() {
  // Which divisions this user runs. The backend scopes the list and refuses
  // the rest anyway; this just keeps the page from offering what would fail.
  // If /me cannot be read, fall back to both and let the backend decide.
  const [myModules, setMyModules] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [module, setModule] = useState("sekretaris");
  const [category, setCategory] = useState("form_pengajuan");
  const [templateFile, setTemplateFile] = useState(null);
  const [fields, setFields] = useState([emptyField()]);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const everyone = MODULE_OPTIONS.map((o) => o.value);
    apiFetch("/api/auth/me/")
      .then((me) => {
        const mine = (me.accessible_modules || []).filter((m) => everyone.includes(m));
        setMyModules(mine.length ? mine : everyone);
      })
      .catch(() => setMyModules(everyone));
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      // manage=1: include the inactive ones, so they can be switched back on.
      const data = await apiFetch("/api/administrasi/templates/?manage=1");
      setTemplates(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = (mod = "sekretaris") => {
    setEditingId(null);
    setName("");
    setModule(mod);
    setCategory(CATEGORY_OPTIONS_BY_MODULE[mod][0].value);
    setTemplateFile(null);
    setFields([emptyField()]);
    setIsActive(true);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setName(t.name);
    setModule(t.module);
    setCategory(t.category);
    setTemplateFile(null);
    setFields(t.fields && t.fields.length ? t.fields.map((f) => ({ ...f })) : [emptyField()]);
    setIsActive(t.is_active);
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => setShowForm(false);

  const handleModuleChange = (v) => {
    setModule(v);
    setCategory(CATEGORY_OPTIONS_BY_MODULE[v][0].value);
  };

  const updateField = (idx, patch) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const addField = () => setFields((prev) => [...prev, emptyField()]);
  const removeField = (idx) => setFields((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) {
      setFormError("Nama template wajib diisi.");
      return;
    }
    if (!editingId && !templateFile) {
      setFormError("File template (docx/pptx/xlsx) wajib diunggah.");
      return;
    }
    const cleanFields = fields
      .filter((f) => f.key.trim() && f.label.trim())
      .map((f) => ({ ...f, key: f.key.trim(), label: f.label.trim() }));
    if (cleanFields.length === 0) {
      setFormError("Tambahkan minimal 1 field.");
      return;
    }
    const keys = cleanFields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      setFormError("Ada key field yang duplikat.");
      return;
    }
    if (cleanFields.some((f) => !/^[a-zA-Z0-9_]+$/.test(f.key))) {
      setFormError("Key field hanya boleh huruf, angka, underscore (tanpa spasi).");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("module", module);
      fd.append("category", category);
      fd.append("is_active", isActive ? "true" : "false");
      fd.append("fields", JSON.stringify(cleanFields));
      if (templateFile) fd.append("template_file", templateFile);

      if (editingId) {
        await apiFetch(`/api/administrasi/templates/${editingId}/`, { method: "PATCH", body: fd });
      } else {
        await apiFetch("/api/administrasi/templates/", { method: "POST", body: fd });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await apiFetch(`/api/administrasi/templates/${t.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !t.is_active }),
      });
      fetchAll();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Hapus template "${t.name}" secara permanen? Tindakan ini tidak bisa dibatalkan.`)) {
      return;
    }
    try {
      await apiFetch(`/api/administrasi/templates/${t.id}/`, { method: "DELETE" });
      fetchAll();
    } catch (err) {
      // Backend menolak dengan pesan jelas kalau template ini sudah pernah
      // dipakai (masih ada riwayat submission yang mereferensikannya) —
      // dalam kasus itu, pakai "Nonaktifkan" saja.
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Template Surat &amp; Pengajuan</h1>
          <p className="text-sm text-muted mt-1">
            Setiap jenis surat/pengajuan = 1 template (file docx/pptx/xlsx berisi tag{" "}
            <code>{"{{field_key}}"}</code>) + daftar field-nya. Menambah jenis baru cukup di
            sini, tanpa perlu ubah kode.
          </p>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      <div className="space-y-8">
        {MODULE_SECTIONS.filter((sec) => myModules?.includes(sec.value)).map((sec) => {
          const catOrder = CATEGORY_OPTIONS_BY_MODULE[sec.value].map((c) => c.value);
          const items = templates
            .filter((t) => t.module === sec.value)
            .sort((a, b) => catOrder.indexOf(a.category) - catOrder.indexOf(b.category));

          return (
            <section key={sec.value}>
              <div className="flex items-center justify-between gap-4 mb-3 pb-2 border-b border-white/30 dark:border-white/10">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${sec.dot}`} />
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold leading-tight">
                      {sec.label}{" "}
                      <span className="text-sm font-normal text-muted">({items.length})</span>
                    </h2>
                    <p className="text-xs text-muted">{sec.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => openCreate(sec.value)}
                  className="shrink-0 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
                >
                  + Template {sec.label}
                </button>
              </div>

              <div className="space-y-2">
                {items.map((t) => (
                  <div
                    key={t.id}
                    className={`glass-card p-4 flex items-center justify-between gap-4 border-l-4 ${sec.border}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{t.name}</span>
                        {!t.is_active && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-400/20 text-gray-500">
                            Nonaktif
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted">
                        {CATEGORY_LABELS[t.category] || t.category} &middot;{" "}
                        {(t.fields || []).length} field
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => openEdit(t)}
                        className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(t)}
                        className={`text-xs px-2 py-1 rounded text-white ${
                          t.is_active
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        {t.is_active ? "Nonaktifkan" : "Aktifkan"}
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        title="Hapus permanen (ditolak otomatis jika template ini sudah pernah dipakai)"
                        className="text-xs px-2 py-1 rounded border border-red-400/50 text-red-600 hover:bg-red-500/10"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                ))}
                {!loading && items.length === 0 && (
                  <p className="text-sm text-muted">Belum ada template {sec.label}.</p>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? "Edit Template" : "Tambah Template"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium mb-1">Nama Template</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                    placeholder="mis. Surat Peminjaman Gudang"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Modul</label>
                  <CustomSelect
                    value={module}
                    onChange={handleModuleChange}
                    options={MODULE_OPTIONS.filter((o) => myModules?.includes(o.value))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Kategori</label>
                  <CustomSelect
                    value={category}
                    onChange={setCategory}
                    options={CATEGORY_OPTIONS_BY_MODULE[module]}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium mb-1">
                    File Template (.docx / .pptx / .xlsx){" "}
                    {editingId && (
                      <span className="text-muted font-normal">
                        — kosongkan jika tidak ganti file
                      </span>
                    )}
                  </label>
                  <input
                    type="file"
                    accept=".docx,.pptx,.xlsx"
                    onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium">Daftar Field</label>
                  <button
                    type="button"
                    onClick={addField}
                    className="text-xs px-2 py-1 rounded bg-brand-600 text-white hover:bg-brand-700"
                  >
                    + Field
                  </button>
                </div>
                <div className="space-y-2">
                  {fields.map((f, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={f.key}
                        onChange={(e) => updateField(idx, { key: e.target.value })}
                        placeholder="key (mis. nama_pemohon)"
                        className="col-span-3 rounded-md border border-white/50 dark:border-white/15 px-2 py-1.5 text-xs font-mono"
                      />
                      <input
                        type="text"
                        value={f.label}
                        onChange={(e) => updateField(idx, { label: e.target.value })}
                        placeholder="Label (mis. Nama Pemohon)"
                        className="col-span-4 rounded-md border border-white/50 dark:border-white/15 px-2 py-1.5 text-xs"
                      />
                      <div className="col-span-3">
                        <CustomSelect
                          value={f.type}
                          onChange={(v) => updateField(idx, { type: v })}
                          options={FIELD_TYPE_OPTIONS}
                        />
                      </div>
                      <label className="col-span-1 flex items-center justify-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={!!f.required}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeField(idx)}
                        className="col-span-1 text-red-500 hover:text-red-700 text-xs"
                      >
                        Hapus
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted mt-2">
                  Tag di dalam file template harus persis <code>{"{{key}}"}</code> (mis.{" "}
                  <code>{"{{nama_pemohon}}"}</code>) — di paragraf/tabel Word, teks/tabel di
                  PowerPoint, atau di dalam cell Excel. Centang di kolom paling kanan = field
                  wajib diisi.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Template aktif
              </label>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
